
/*!
 * Copyright (C) 2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2024-05-17 (jhoward)
 *			- Refactor calendar day from hotelCalendar.vue to hotelCalendarDay.vue.
 */

import {defineComponent, PropType} from 'vue'

export interface CalendarDay {
  PRICE: number;
  BASE_PRICE: number;
  // HIGHLIGHT: boolean;
  // CHECK_IN: boolean;
  // CHECK_OUT: boolean;
  DISPLAY_RATES: boolean;
  OFFER_CODE: string;
  OFFER_CODE_STRIKE: string;
  ROOM_SEQUENCE_STRIKE: string;
  MIN_NIGHTS: number;
  TITLE: string;
  BLANK: boolean;
  KEY: string; //YYYY-MM-DD
  DATE: Date | null;
  BEFORE_TODAY: boolean | undefined;
}

export default defineComponent({
  name: 'hotelCalendarDay',
  emits: ['close'],
  props: {
    day: {
      type: Object as PropType<CalendarDay>,
      required: true,
    },
  },
  computed: {
    component(): string {
      return (!this.isBlank) ? 'a' : 'span';
    },
    isCheckIn(): boolean {
      return !!this.selectedDates.length && this.selectedDates[0] === this.day.KEY
    },
    isCheckOut(): boolean {
      return this.selectedDates.length > 1 && this.selectedDates[this.selectedDates.length - 1] === this.day.KEY
    },
    selectedDates(): string[] {
      return this.$store.getters.selectedDates;
    },
    ariaHidden(): string | null {
      return (this.day.BLANK) ? 'true' : null;
    },
    ariaLabel(): string | null {
      let output = null;
      if (!this.day.BLANK) {
        const suffix = (this.day.DATE) ? window.pepDateFunctions.dateSuffix(this.day.DATE?.getDate()) : '';
        output = this.getDayMonthName(this.day) + ' ' + this.day.TITLE + suffix;
        if (this.isCheckIn) {
          output += ', check in day';
        } else if (this.isCheckOut) {
          output += ', check out day';
        } else if (this.selectedDates.includes(this.day.KEY)) {
          const dayNumberOfReservation = this.selectedDates.indexOf(this.day.KEY) + 1;
          output += ', ' + dayNumberOfReservation + window.pepDateFunctions.dateSuffix(dayNumberOfReservation) + ' day of reservation';
        }
      }

      return output;
    },
    href(): string | null {
      return (this.isBlank) ? null : '#';
    },
    dayClasses(): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {
        'day--container-blank': this.isBlank,
        'day--container-check_in': this.isCheckIn,
        'day--container-check_out': this.isCheckOut,
        'day--container-highlight': this.isHighlighted,
        'day--container-no_rates': !this.day.DISPLAY_RATES,
      };

      if (!this.isBlank) {
        if (this.isNoData) {
          output['day--container-no_data'] = true;
        } else {
          if (this.isSpecialOffer) {
            output['day--container-special_offer'] = true;
          }
        }
      }

      return output;
    },
    isSpecialOffer(): boolean {
      return (this.day.DISPLAY_RATES && this.day.PRICE !== this.day.BASE_PRICE)
        || this.day.OFFER_CODE !== this.day.OFFER_CODE_STRIKE;
    },
    isBlank(): boolean {
      return this.day.BLANK || !!this.day.BEFORE_TODAY;
    },
    isHighlighted(): boolean {
      return this.selectedDates.includes(this.day.KEY);
    },
    isNoData(): boolean {
      return this.isBlank || (this.day.DISPLAY_RATES && this.day.PRICE === 0);
    },
  },
  methods: {
    getDayMonthName(day: CalendarDay): string {
      const month = day.DATE?.getMonth();
      if (month) {
        return window.pepDateFunctions.language.en.months[month];
      }

      return '';
    },

    handleDayKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleDayClick();
      } else if (event.key === 'Escape') {
        this.$emit('close');
      }
    },
    handleDayClick() {
      if (!this.isBlank) {
        if (this.day.DATE) {
          if (this.$store.state.checkInDate && this.$store.state.checkOutDate && !this.isNoData) {
            //both dates selected, start over
            this.$store.commit('updateCheckInDate', this.day.DATE);
            this.$store.commit('updateCheckOutDate', null);
          } else if (this.$store.state.checkInDate && !this.$store.state.checkOutDate) {
            //only check in selected
            if (this.day.DATE.getTime() < this.$store.state.checkInDate.getTime()) {
              this.$store.commit('updateCheckOutDate', this.$store.state.checkInDate);
              this.$store.commit('updateCheckInDate', this.day.DATE);
            } else {
              this.$store.commit('updateCheckOutDate', this.day.DATE);
            }

          } else if (!this.isNoData) {
            //fallback to setting up check in
            this.$store.commit('updateCheckInDate', this.day.DATE);
            this.$store.commit('updateCheckOutDate', null);
          }
        }
      }
    },
  }
})
